import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAxios } from '@/hooks/useAxios';
import { useMy } from '@/hooks/useMy';
import { useTutorialEnabled } from '@/hooks/useTutorialEnabled';

import { getWelcomeTutorialSteps } from './steps';
import { useTutorial } from './TutorialProvider';

function useWelcomeTutorialSetting() {
  const { data: settings, status } = useMy(
    {
      resource: 'setting',
      query: '#setting#show-welcome-tutorial',
      user: true,
    },
    { enabled: true }
  );
  const showTutorial = settings?.find(s => s.name === 'show-welcome-tutorial');

  return {
    isNewUser: status === 'success' && String(showTutorial?.value) === 'true',
    isLoading: status === 'pending',
  };
}

function useDismissWelcomeTutorial() {
  const axios = useAxios();
  const { invalidate } = useMy(
    { resource: 'setting', user: true },
    { enabled: false }
  );

  return async () => {
    await axios.put(
      '/setting',
      { name: 'show-welcome-tutorial', value: false },
      {
        params: { user: 'true' },
      }
    );
    invalidate();
  };
}

export function WelcomeModal() {
  const enabled = useTutorialEnabled();
  const { isActive, startTutorial } = useTutorial();
  const { isNewUser, isLoading } = useWelcomeTutorialSetting();
  const dismissBackend = useDismissWelcomeTutorial();
  const startedByWelcome = useRef(false);
  const prevIsActive = useRef(false);
  const [dismissed, setDismissed] = useState(false);
  const hasSteps = getWelcomeTutorialSteps().length > 0;

  useEffect(() => {
    if (isActive) {
      prevIsActive.current = true;
    } else if (prevIsActive.current) {
      prevIsActive.current = false;
      if (startedByWelcome.current) {
        startedByWelcome.current = false;
        dismissBackend();
      }
    }
  }, [isActive]);

  if (!enabled) return null;
  if (isLoading) return null;

  const open = hasSteps && isNewUser && !isActive && !dismissed;

  function handleStart() {
    startedByWelcome.current = true;
    setDismissed(true);
    startTutorial();
  }

  function handleSkip() {
    dismissBackend();
  }

  return (
    <Dialog open={open} onOpenChange={() => handleSkip()}>
      <DialogContent
        className="sm:max-w-md"
        onPointerDownOutside={e => e.preventDefault()}
        onEscapeKeyDown={e => e.preventDefault()}
      >
        <div className="flex justify-center pt-2">
          <img
            src="/icons/praetorian-guards.png"
            alt="Praetorian Guards"
            className="h-32 object-contain"
          />
        </div>

        <DialogHeader>
          <DialogTitle className="text-lg">
            Welcome to Praetorian Guard!
          </DialogTitle>
          <DialogDescription className="text-chariot-text-secondary text-sm">
            Take a quick tour to learn where everything lives — your seeds,
            assets, risks, and integrations. It only takes a minute.
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className="flex-row justify-end gap-2 sm:flex-row">
          <Button variant="ghost" onClick={handleSkip}>
            Skip for now
          </Button>
          <Button onClick={handleStart}>Start tour</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
